import { useEffect } from "react";
import {
  DEFAULT_THEME,
  DESIGN_TOKENS,
  type ThemeConfig,
} from "@autopic/shared";

/**
 * Applies a garage's ThemeConfig to CSS custom properties on a target element
 * (the app root by default), so brand colours / radius / fonts override the
 * built-in cream-and-green defaults. The defaults already live in global.css;
 * we only set variables that the theme actually overrides.
 */

interface ThemeProviderProps {
  theme?: ThemeConfig;
  /** Element the variables are written to. Defaults to document root. */
  target?: HTMLElement | null;
  children: React.ReactNode;
}

/** Lighten/darken a hex colour by mixing toward white/black — for hover states. */
function applyTheme(el: HTMLElement, theme: ThemeConfig): void {
  const set = (name: string, value: string | number | undefined) => {
    if (value === undefined || value === "") return;
    el.style.setProperty(name, String(value));
  };

  set("--ap-primary", theme.primary);
  set("--ap-green", theme.primary);
  set("--ap-primary-dark", theme.primaryDark);
  set("--ap-green-deep", theme.primaryDark);
  set("--ap-accent", theme.accent);
  set("--ap-gold", theme.accent);
  set("--ap-bg", theme.background);
  set("--ap-cream", theme.background);
  set("--ap-surface", theme.surface);
  set("--ap-white", theme.surface);
  set("--ap-text", theme.ink);
  set("--ap-ink", theme.ink);
  set("--ap-text-soft", theme.inkSoft);
  set("--ap-ink-soft", theme.inkSoft);
  if (typeof theme.radius === "number") {
    set("--ap-radius", `${theme.radius}px`);
    set("--ap-radius-sm", `${Math.max(6, Math.round(theme.radius * 0.62))}px`);
  }
  set("--ap-display", theme.displayFont);
  set("--ap-body", theme.bodyFont);
}

/** Ensure the brand fonts are loaded once (idempotent). */
function ensureFonts(): void {
  if (typeof document === "undefined") return;
  if (document.getElementById("ap-fonts")) return;
  // index.html already ships the same Google Fonts <link> in most surfaces;
  // don't add a duplicate request if it's present.
  if (document.querySelector('link[href*="fonts.googleapis.com/css2"][href*="Fraunces"]')) {
    return;
  }

  const pre1 = document.createElement("link");
  pre1.rel = "preconnect";
  pre1.href = "https://fonts.googleapis.com";

  const pre2 = document.createElement("link");
  pre2.rel = "preconnect";
  pre2.href = "https://fonts.gstatic.com";
  pre2.crossOrigin = "anonymous";

  const css = document.createElement("link");
  css.id = "ap-fonts";
  css.rel = "stylesheet";
  css.href = DESIGN_TOKENS.fontHref;

  document.head.append(pre1, pre2, css);
}

export function ThemeProvider({ theme, target, children }: ThemeProviderProps) {
  useEffect(() => {
    ensureFonts();
  }, []);

  useEffect(() => {
    const el = target ?? document.documentElement;
    applyTheme(el, theme ?? DEFAULT_THEME);
  }, [theme, target]);

  return <>{children}</>;
}
